import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Dispatch, memo, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { IRequirementStructuralUnitItem } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { ISuEditorForm } from 'KnmPackage/StructureRequirementsNpaModule/interface';
import { requirementStructuralUnitTypes } from 'KnmPackage/RequirementsNpaModalModule/components/requirementStructuralUnitTypes';
import {
  createMandatoryListAction,
  createSusAction,
  createSusWithMandatoryAction,
} from 'KnmPackage/RequirementsNpaModalModule/actions/requirementsNpaActions';
import { IRequirementsMandatoryRaw } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementsMandatory';

import styles from './CreateSuAndMandatoryModal.module.scss';
import { SuValidator } from './SuValidator';
import { CreateSuAndMandatoryModalForm } from './CreateSuAndMandatoryModalForm';

interface ICreateSuAndMandatoryModalProps {
  onClose: (suId?: string, newMandatoryId?: string) => void;
  npaItem: IRequirementNpa;
  withRequirements: boolean;
  knoVersionId: string;
  supervisionVersionId: string;
  addMandatoryPath: string;
  suId: number;
  structuresUnitsFilters?: {
    objectTypeIds?: string[];
    objectKindIds?: string[];
    objectSubkindIds?: string[];
    riskIndicatorIds?: string[];
  };
}

export const CreateSuAndMandatoryModal = memo(
  ({
    onClose,
    npaItem,
    withRequirements,
    knoVersionId,
    supervisionVersionId,
    addMandatoryPath,
    suId,
    structuresUnitsFilters,
  }: ICreateSuAndMandatoryModalProps) => {
    const dispatch = useDispatch<Dispatch<RootActions>>();

    const handleSubmit = (values: ISuEditorForm) => {
      const callback = (suIdNew: number, newMandatoryId?: number) => {
        onClose(suIdNew.toString(), newMandatoryId?.toString());
      };

      if (addMandatoryPath) {
        const mandatoryList: IRequirementsMandatoryRaw[] = (
          values.newStructuralUnits[0].mandatoryRequirements || []
        ).map(mandatory => {
          return {
            title: mandatory,
            isSelected: false,
            structuralUnit: {
              id: suId,
              label: '',
              value: -1,
            },
            oivControlId: knoVersionId,
            oivControlTypeId: supervisionVersionId,
            ...structuresUnitsFilters,
          };
        });

        dispatch(createMandatoryListAction(mandatoryList, npaItem.id, mandatoryId => callback(suId, mandatoryId)));
      } else if (withRequirements) {
        dispatch(
          createSusWithMandatoryAction(
            npaItem.id,
            knoVersionId,
            supervisionVersionId,
            structuresUnitsFilters || {},
            values.newStructuralUnits,
            callback,
          ),
        );
      } else {
        dispatch(createSusAction(npaItem.id, values.newStructuralUnits, callback));
      }
    };

    const initValue = useMemo(() => {
      const val: ISuEditorForm = {
        newStructuralUnits: [{ components: [{ selectType: null, type: '', value: '', uuid: uuidv4() }] }],
      };

      if (addMandatoryPath) {
        const array = addMandatoryPath.split('\\');
        const components: IRequirementStructuralUnitItem[] = array
          .map(item => {
            const pairs = item.split(':');
            const option = requirementStructuralUnitTypes.find(
              i => i.label.toLowerCase().indexOf(pairs[0].toLowerCase()) > -1,
            );

            if (option) {
              const resultObj: IRequirementStructuralUnitItem = {
                selectType: option,
                type: option.value,
                value: pairs[1],
                uuid: uuidv4(),
              };

              return resultObj;
            }

            const resultObj: IRequirementStructuralUnitItem = {
              selectType: requirementStructuralUnitTypes[0],
              type: '-1',
              value: pairs[1],
              uuid: uuidv4(),
            };

            return resultObj;
          })
          .filter(i => i?.value !== '-1');

        val.newStructuralUnits[0].components = components;
      }

      if (withRequirements) {
        val.newStructuralUnits[0].mandatoryRequirements = [''];
      }

      return val;
    }, [withRequirements, addMandatoryPath]);

    return (
      <>
        <Modal visible={true} onClose={onClose} modalBodyClassName={styles.Modal}>
          <Form<ISuEditorForm>
            render={props => {
              return (
                <CreateSuAndMandatoryModalForm
                  {...props}
                  onClose={onClose}
                  npaItem={npaItem}
                  withRequirements={withRequirements}
                  disabledEditSu={!!addMandatoryPath}
                />
              );
            }}
            onSubmit={handleSubmit}
            mutators={{ ...arrayMutators }}
            validate={SuValidator}
            initialValues={initValue}
          />
        </Modal>
      </>
    );
  },
);
